// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@pace/App/Components/Common/XngZone";
import Button, { ButtonPresentationProps } from "@pace/MobiusOverrides/Button/Button";
import { safeJsonParse } from "@pace/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        currentBillTo: getBillToState(state, session.billToId).value,
        displayChangeCustomerLink: session.displayChangeCustomerLink,
        isPunchOutSession: getIsPunchOutSession(state),
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        warehouseInformation: safeJsonParse(session.properties["headerWarehouseInformation"]) || {},
    };
};

export interface MyAccountDashboardSideNavStyles {
    container?: InjectableCss;
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        shipToInfo?: TypographyPresentationProps;
        address?: TypographyPresentationProps;
        link?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
        changeCustomerButton?: ButtonPresentationProps;
    };
    linkGroup?: InjectableCss;
}

export const myAccountDashboardSideNavStyles: MyAccountDashboardSideNavStyles = {};
const styles = myAccountDashboardSideNavStyles;

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const MyAccountDashboardSideNav = (props: Props) => {
    const history = useHistory();

    const onSignOutHandler = () => {
        props.isPunchOutSession ? props.cancelPunchOut() : props.signOut();
    };
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.personalInfo?.wrapper}>
                <Typography {...styles.personalInfo?.title}>{translate("My Account")}</Typography>
                <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                    <Typography {...styles.personalInfo?.shipToInfo}>
                        {props.currentBillTo?.customerNumber} - {props.currentBillTo?.customerName}
                    </Typography>
                    <Typography {...styles.personalInfo?.address}>{props.currentBillTo?.fullAddress}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.linkGroup}>
                <XngZone contentId={props.id} zoneName="Content01" hint="Zone For Links" />
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler();
                    }}
                    aria-label="Log Out"
                    data-test-selector="myAccountDashboardLogOutLink"
                >
                    {props.isPunchOutSession ? translate("Cancel PunchOut") : translate("Log Out")}
                </Link>
                {props.displayChangeCustomerLink && (
                    <Button
                        variant="primary"
                        {...styles.actionSection?.changeCustomerButton}
                        onClick={() => {
                            props.changeCustomerUrl && history.push(props.changeCustomerUrl);
                        }}
                        aria-label="Change Account"
                    >
                        {translate("Change Account")}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountDashboardSideNav),
    definition: {
        group: "Basic",
        icon: "link-simple",
    },
};
export default widgetModule;
